import React, { useState, useEffect } from 'react';
import { Search, User as UserIcon, Shield, LogIn, LogOut, Download, Sun, Moon, Globe, Clock, Server, Menu, Dices, BarChart2, Users, Compass, Palette } from 'lucide-react';
import { api, type User, type UserProfile } from '../lib/api';
import { DownloadsDrawer } from './DownloadsDrawer';
import { ServerConfigModal } from './ServerConfigModal';
import { MobileSideMenu } from './MobileSideMenu';
import { HomeflixLogo } from './HomeflixLogo';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

export interface NavbarProps {
  user: User | null;
  activeProfile?: UserProfile | null;
  activeNav: string;
  onNavigate: (page: string) => void;
  onOpenSearch: () => void;
  onOpenAuth: () => void;
  onOpenProfiles?: () => void;
  onOpenStats?: () => void;
  onSurpriseMe?: () => void;
  onLogout: () => void;
}

export function Navbar({
  user,
  activeProfile,
  activeNav,
  onNavigate,
  onOpenSearch,
  onOpenAuth,
  onOpenProfiles,
  onOpenStats,
  onSurpriseMe,
  onLogout,
}: NavbarProps) {
  const { theme, toggleTheme, accentTheme, accentThemes, setAccentTheme, language, toggleLanguage, t } = useThemeLanguage();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [showDownloads, setShowDownloads] = useState(false);
  const [showServerModal, setShowServerModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeDownloadsCount, setActiveDownloadsCount] = useState(0);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    // Server-side download management is admin-only (guarded server-side
    // too) — regular users never poll this endpoint.
    if (!isAdmin) return;

    const checkActive = () => {
      api
        .getDownloads()
        .then((jobs) => {
          const active = jobs.filter((j) => j.status === 'downloading' || j.status === 'queued');
          setActiveDownloadsCount(active.length);
        })
        .catch(() => {});
    };

    checkActive();
    const interval = setInterval(checkActive, 4000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 safe-top glass-heavy border-b border-white/5">
      <div>
        <nav className="mx-auto flex h-14 md:h-16 max-w-[1920px] items-center justify-between px-3 md:px-8">
          {/* Logo & Desktop navigation */}
          <div className="flex items-center gap-8 shrink-0">
            <button
              onClick={() => onNavigate('home')}
              className="flex items-center gap-2 shrink-0 cursor-pointer"
              aria-label="Homeflix Home"
            >
              <HomeflixLogo theme={theme} className="h-7 md:h-8 w-auto" />
            </button>

            {/* Center navigation — hidden on mobile */}
            <div className="hidden md:flex items-center gap-6">
              <button
                onClick={() => onNavigate('home')}
                className={`text-sm font-semibold transition-colors ${
                  activeNav === 'home' ? 'text-white font-bold' : 'text-neutral-400 hover:text-white'
                }`}
              >
                {t('home')}
              </button>
              <button
                onClick={() => onNavigate('explore')}
                className={`text-sm font-semibold transition-colors flex items-center gap-1.5 cursor-pointer ${
                  activeNav === 'explore' ? 'text-accent-primary font-bold' : 'text-neutral-400 hover:text-white'
                }`}
              >
                <Compass className="w-3.5 h-3.5 text-accent-primary" />
                <span>{t('explore')}</span>
              </button>
              <button
                onClick={() => onNavigate('movies')}
                className={`text-sm font-semibold transition-colors ${
                  activeNav === 'movies' ? 'text-white font-bold' : 'text-neutral-400 hover:text-white'
                }`}
              >
                {t('movies')}
              </button>
              <button
                onClick={() => onNavigate('shows')}
                className={`text-sm font-semibold transition-colors ${
                  activeNav === 'shows' ? 'text-white font-bold' : 'text-neutral-400 hover:text-white'
                }`}
              >
                {t('shows')}
              </button>
              <button
                onClick={() => onNavigate('watchlist')}
                className={`text-sm font-semibold transition-colors cursor-pointer ${
                  activeNav === 'watchlist' ? 'text-white font-bold' : 'text-neutral-400 hover:text-white'
                }`}
              >
                {t('watchlist')}
              </button>
              <button
                onClick={() => onNavigate('history')}
                className={`text-sm font-semibold transition-colors cursor-pointer flex items-center gap-1.5 ${
                  activeNav === 'history' ? 'text-white font-bold' : 'text-neutral-400 hover:text-white'
                }`}
              >
                <Clock className="w-3.5 h-3.5 text-accent-primary" />
                <span>{t('watchHistory')}</span>
              </button>
              <button
                onClick={() => onNavigate('downloads')}
                className={`text-sm font-semibold transition-colors cursor-pointer flex items-center gap-1.5 ${
                  activeNav === 'downloads' ? 'text-white font-bold' : 'text-neutral-400 hover:text-white'
                }`}
              >
                <span>{language === 'he' ? 'הורדות במכשיר' : 'Downloads'}</span>
              </button>
            </div>
          </div>

          {/* Mobile Right Actions: Quick Server Icon + Hamburger Menu (clean, spacious, no overlap) */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setShowServerModal(true)}
              className="p-2 rounded-xl text-neutral-300 hover:text-white bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition-all cursor-pointer relative header-btn"
              title={language === 'he' ? 'הגדרות שרת' : 'Server Settings'}
              aria-label="Server Settings"
            >
              <Server className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </button>

            <button
              onClick={() => setShowMobileMenu(true)}
              className="p-2 rounded-xl text-white bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition-all cursor-pointer header-btn"
              title={language === 'he' ? 'תפריט' : 'Menu'}
              aria-label="Open Menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          {/* Desktop Right actions (hidden on mobile) */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher (Hebrew / English) */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs font-semibold transition-all border border-white/10 cursor-pointer select-none"
              title={language === 'en' ? 'Switch to Hebrew' : 'החלף לאנגלית'}
              aria-label="Toggle Language"
            >
              <Globe className="w-3.5 h-3.5 text-neutral-400" />
              <span className={language === 'he' ? 'text-accent-primary font-bold' : 'text-neutral-400'}>עב</span>
              <span className="text-white/20">|</span>
              <span className={language === 'en' ? 'text-accent-primary font-bold' : 'text-neutral-400'}>EN</span>
            </button>

            {/* Theme Switcher (Dark / Light) */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full text-neutral-400 hover:text-white hover:bg-white/10 transition-all cursor-pointer border border-transparent hover:border-white/10"
              title={theme === 'dark' ? t('lightMode') : t('darkMode')}
              aria-label="Toggle Theme"
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 text-amber-400 transition-transform duration-300 hover:rotate-90 hover:scale-110" />
              ) : (
                <Moon className="h-4 w-4 text-indigo-400 transition-transform duration-300 hover:-rotate-45 hover:scale-110" />
              )}
            </button>

            {/* Color Accent Theme Selector */}
            <div className="relative">
              <button
                onClick={() => setShowThemePicker(!showThemePicker)}
                className="p-1.5 rounded-full text-neutral-400 hover:text-white hover:bg-white/10 transition-all cursor-pointer border border-transparent hover:border-white/10 flex items-center justify-center"
                title={t('themeSelector')}
                aria-label="Color Theme"
              >
                <div
                  className="w-4 h-4 rounded-full border border-white/40 shadow-sm"
                  style={{ backgroundColor: accentThemes.find((at) => at.id === accentTheme)?.primary || '#E50914' }}
                />
              </button>
              {showThemePicker && (
                <div className="absolute right-0 mt-2 p-2 rounded-xl bg-neutral-950/95 border border-white/15 shadow-2xl backdrop-blur-xl z-50 flex items-center gap-2 animate-in fade-in">
                  {accentThemes.map((at) => (
                    <button
                      key={at.id}
                      onClick={() => {
                        setAccentTheme(at.id);
                        setShowThemePicker(false);
                      }}
                      className={`w-5 h-5 rounded-full border transition-all cursor-pointer hover:scale-125 ${
                        accentTheme === at.id ? 'border-white scale-110 ring-2 ring-white/50' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: at.primary }}
                      title={language === 'he' ? at.nameHe : at.name}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Admin Button - strictly visible ONLY to ADMIN role */}
            {user?.role === 'admin' && (
              <button
                onClick={() => onNavigate('admin')}
                className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors cursor-pointer ${
                  activeNav === 'admin'
                    ? 'bg-accent-primary text-white border-accent-primary shadow-md shadow-accent-glow'
                    : 'bg-white/5 text-neutral-300 border-white/10 hover:bg-white/15'
                }`}
                title="Server Control Panel (127.0.0.1:8097)"
              >
                <Shield className="w-3.5 h-3.5 text-accent-primary" />
                <span>{t('admin')}</span>
              </button>
            )}

            {/* Downloads Drawer Button — server torrent downloads are admin-only */}
            {isAdmin && (
              <button
                onClick={() => setShowDownloads(true)}
                className="relative rounded-full p-2 text-neutral-400 hover:text-white transition-colors cursor-pointer"
                title={t('downloadsManager')}
                aria-label="Downloads"
              >
                <Download className="h-5 w-5" />
                {activeDownloadsCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent-primary text-[10px] font-bold text-white">
                    {activeDownloadsCount}
                  </span>
                )}
              </button>
            )}

            <button
              onClick={() => setShowServerModal(true)}
              className="relative rounded-full p-2 text-neutral-400 hover:text-white transition-colors cursor-pointer"
              title={language === 'he' ? 'הגדרות שרת' : 'Server Settings'}
              aria-label="Server Settings"
            >
              <Server className="h-5 w-5" />
            </button>

            {onSurpriseMe && (
              <button
                onClick={onSurpriseMe}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-600/30 to-pink-600/30 hover:from-purple-600/50 hover:to-pink-600/50 border border-purple-500/30 text-xs font-bold text-white transition-all shadow-md active:scale-95 cursor-pointer"
                title={language === 'he' ? 'הפתע אותי! בחירת סרט אקראי' : 'Surprise Me! Pick a random title'}
              >
                <Dices className="h-4 w-4 text-pink-400" />
                <span className="hidden lg:inline">{language === 'he' ? 'הפתע אותי' : 'Surprise Me'}</span>
              </button>
            )}

            <button
              onClick={onOpenSearch}
              className="rounded-full p-2 text-neutral-400 hover:text-white transition-colors cursor-pointer"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Profile Picker Quick Button */}
            {activeProfile && onOpenProfiles && (
              <button
                onClick={onOpenProfiles}
                className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold transition-all cursor-pointer"
                title={language === 'he' ? `פרופיל פעיל: ${activeProfile.name} (לחץ להחלפה)` : `Active Profile: ${activeProfile.name} (Click to switch)`}
              >
                <div className="w-5 h-5 rounded-full bg-accent-primary flex items-center justify-center text-[10px] text-white">
                  {activeProfile.avatar === 'avatar-kids' ? '🧸' : activeProfile.name[0]}
                </div>
                <span className="max-w-[80px] truncate hidden xl:inline text-neutral-300">{activeProfile.name}</span>
              </button>
            )}

            {/* User Profile / Login */}
            <div className="relative">
              {user ? (
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center gap-2 rounded-full p-1 text-neutral-300 hover:text-white transition-colors"
                  aria-label="Profile"
                >
                  <div className="h-8 w-8 rounded-full bg-accent-primary flex items-center justify-center font-bold text-xs text-white uppercase shadow-md shadow-accent-glow">
                    {user.displayName?.[0] || user.username[0]}
                  </div>
                </button>
              ) : (
                <button
                  onClick={onOpenAuth}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-primary hover:brightness-110 text-xs font-bold text-white transition-all shadow-md shadow-accent-glow cursor-pointer"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  {t('signIn')}
                </button>
              )}

              {/* User Dropdown */}
              {showUserDropdown && user && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl bg-neutral-900 border border-white/10 shadow-2xl p-2 z-50 text-white animate-in fade-in">
                  <div className="p-3 border-b border-white/10">
                    <p className="text-sm font-bold truncate">{user.displayName || user.username}</p>
                    <p className="text-xs text-neutral-400 truncate">@{user.username}</p>
                    <span className={`inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-extrabold uppercase ${
                      user.role === 'admin'
                        ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
                        : 'bg-white/10 text-neutral-300'
                    }`}>
                      {user.role === 'admin' ? (language === 'he' ? 'מנהל מערכת' : 'ADMINISTRATOR') : (language === 'he' ? 'משתמש רשום' : 'MEMBER')}
                    </span>
                  </div>

                  <button
                    onClick={() => {
                      onNavigate('history');
                      setShowUserDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-neutral-300 hover:text-white hover:bg-white/10 rounded-lg flex items-center gap-2 mt-1 cursor-pointer"
                  >
                    <Clock className="w-4 h-4 text-accent-primary" />
                    <span>{t('watchHistory')}</span>
                  </button>

                  {onOpenStats && (
                    <button
                      onClick={() => {
                        onOpenStats();
                        setShowUserDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-neutral-300 hover:text-white hover:bg-white/10 rounded-lg flex items-center gap-2 mt-1 cursor-pointer"
                    >
                      <BarChart2 className="w-4 h-4 text-amber-400" />
                      <span>{language === 'he' ? 'סטטיסטיקות צפייה' : 'My Watch Stats'}</span>
                    </button>
                  )}

                  {onOpenProfiles && (
                    <button
                      onClick={() => {
                        onOpenProfiles();
                        setShowUserDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-neutral-300 hover:text-white hover:bg-white/10 rounded-lg flex items-center gap-2 mt-1 cursor-pointer"
                    >
                      <Users className="w-4 h-4 text-purple-400" />
                      <span>{language === 'he' ? 'החלף פרופיל' : 'Switch Profile'}</span>
                    </button>
                  )}

                  {user.role === 'admin' && (
                    <button
                      onClick={() => {
                        onNavigate('admin');
                        setShowUserDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-neutral-300 hover:text-white hover:bg-white/10 rounded-lg flex items-center gap-2 mt-1 cursor-pointer"
                    >
                      <Shield className="w-4 h-4 text-[#E50914]" />
                      <span>{language === 'he' ? 'לוח בקרה לניהול' : 'Server Dashboard'}</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setShowServerModal(true);
                      setShowUserDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-neutral-300 hover:text-white hover:bg-white/10 rounded-lg flex items-center gap-2 mt-1 cursor-pointer"
                  >
                    <Server className="w-4 h-4 text-[#E50914]" />
                    <span>{language === 'he' ? 'הגדרות שרת' : 'Server Settings'}</span>
                  </button>

                  <button
                    onClick={() => {
                      onLogout();
                      setShowUserDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-red-400 hover:bg-red-500/10 rounded-lg flex items-center gap-2 mt-1 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t('signOut')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Slide-over Side Drawer Menu */}
      <MobileSideMenu
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        user={user}
        activeProfile={activeProfile}
        activeNav={activeNav}
        onNavigate={onNavigate}
        onOpenAuth={onOpenAuth}
        onOpenProfiles={onOpenProfiles}
        onOpenStats={onOpenStats}
        onSurpriseMe={onSurpriseMe}
        onOpenDownloads={() => setShowDownloads(true)}
        onOpenServerModal={() => setShowServerModal(true)}
        onLogout={onLogout}
        activeDownloadsCount={activeDownloadsCount}
      />

      {/* Downloads Slide-over Drawer */}
      <DownloadsDrawer isOpen={showDownloads} onClose={() => setShowDownloads(false)} />

      {/* Server Config Modal */}
      <ServerConfigModal isOpen={showServerModal} onClose={() => setShowServerModal(false)} />
    </header>
  );
}

